import React, { useState, useEffect } from 'react';
import {
  Image, FileText, Droplets, Upload,
  AlertCircle, Plus, Clock, CheckCircle2,
} from 'lucide-react';
import DesignTemplateLibrary from '../components/DesignTemplateLibrary';
import BrandStandards from '../components/BrandStandards';
import AssetRequests from '../components/AssetRequests';
import { assetRequestsService } from '../api/assetRequestsApi';
import { designTemplatesService } from '../api/designTemplatesApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const TABS = [
  { id: 'overview',    label: 'Overview'     },
  { id: 'requests',    label: 'Requests'     },
  { id: 'library',     label: 'Asset Library'},
  { id: 'inspiration', label: 'Inspiration'  },
  { id: 'brand',       label: 'Brand'        },
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';

const Skele = () => (
  <div className="h-5 w-10 rounded animate-pulse"
       style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
);

const GraphicsPage = () => {
  const [activeTab,       setActiveTab]       = useState('overview');
  const [requests,        setRequests]        = useState([]);
  const [templates,       setTemplates]       = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Load overview data once (and whenever tab returns to overview)
  useEffect(() => {
    if (activeTab !== 'overview') return;
    let mounted = true;
    (async () => {
      setOverviewLoading(true);
      try {
        const [reqs, tmpls] = await Promise.all([
          assetRequestsService.list(),
          designTemplatesService.list(),
        ]);
        if (mounted) {
          setRequests(reqs  || []);
          setTemplates(tmpls || []);
        }
      } catch { /* non-fatal — page is still usable */ }
      finally { if (mounted) setOverviewLoading(false); }
    })();
    return () => { mounted = false; };
  }, [activeTab]);

  // Derived metrics
  const activeCount    = requests.filter(r => ['new','in_progress','in_review','revisions'].includes(r.status)).length;
  const pendingReview  = requests.filter(r => r.status === 'in_review').length;
  const completedCount = requests.filter(r => r.status === 'completed').length;
  const totalAssets    = templates.length;

  const recentTemplates = [...templates]
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .slice(0, 4);

  const recentRequests = [...requests]
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .slice(0, 4);

  const total = requests.length || 1; // avoid /0
  const approvalRate   = Math.round(requests.filter(r => ['approved','completed'].includes(r.status)).length / total * 100);
  const completionRate = Math.round(completedCount / total * 100);
  const inProgressRate = Math.round(activeCount / total * 100);

  const STATS = [
    { label: 'Active Requests', value: activeCount,   icon: AlertCircle,   tint: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning },
    { label: 'Pending Review',  value: pendingReview, icon: Clock,         tint: '#f3e8ff',                 color: '#7e22ce'              },
    { label: 'Total Assets',    value: totalAssets,   icon: FileText,      tint: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success  },
    { label: 'Completed',       value: completedCount,icon: CheckCircle2,  tint: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info     },
  ];

  const statusStyle = (s) => {
    if (s === 'completed')   return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    if (s === 'approved')    return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    if (s === 'in_progress') return { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info    };
    if (s === 'in_review')   return { backgroundColor: '#f3e8ff',                 color: '#7e22ce'              };
    if (s === 'revisions')   return { backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
    if (s === 'new')         return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
    return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  };

  const priorityStyle = (p) => {
    if (p === 'urgent') return { backgroundColor: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger  };
    if (p === 'high')   return { backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
    if (p === 'medium') return { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info    };
    return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  };

  return (
    <div className="space-y-5">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]"
             style={{ color: BRAND_COLORS.primary }}>
            Creative
          </p>
          <h1 className="mt-0.5 text-2xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            Graphics
          </h1>
        </div>
        {activeTab === 'overview' && (
          <button
            onClick={() => setActiveTab('requests')}
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <Plus size={15} /> New Brief
          </button>
        )}
      </div>

      {/* ── Tab nav ─────────────────────────────────────────────────── */}
      <div className="flex overflow-x-auto border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        {TABS.map(({ id, label }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="flex-shrink-0 px-3 sm:px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors"
              style={{
                borderBottomColor: active ? BRAND_COLORS.primary : 'transparent',
                color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                backgroundColor: 'transparent',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Overview ────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map(({ label, value, icon: Icon, tint, color }) => (
              <div
                key={label}
                className="flex items-center gap-3 px-4 py-4 rounded-xl border"
                style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                     style={{ backgroundColor: tint }}>
                  <Icon size={18} style={{ color }} />
                </div>
                <div>
                  {overviewLoading
                    ? <Skele />
                    : <p className="text-xl font-bold leading-none" style={{ color }}>{value}</p>
                  }
                  <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Assets */}
            <div className="rounded-xl border overflow-hidden"
                 style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              <div className="flex items-center justify-between px-5 py-3.5 border-b"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Recent Assets</h2>
                <button
                  onClick={() => setActiveTab('library')}
                  className="text-xs font-medium"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  View all →
                </button>
              </div>
              <div className="p-4 space-y-2">
                {overviewLoading ? (
                  [0,1,2].map(i => (
                    <div key={i} className="h-10 rounded-xl animate-pulse"
                         style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }} />
                  ))
                ) : recentTemplates.length === 0 ? (
                  <p className="text-sm text-center py-6" style={{ color: SYSTEM_COLORS.textMuted }}>
                    No assets yet —{' '}
                    <button onClick={() => setActiveTab('library')} style={{ color: BRAND_COLORS.primary }}>
                      upload one
                    </button>
                  </p>
                ) : recentTemplates.map((t) => (
                  <div key={t.id}
                       className="flex items-center justify-between px-3 py-2.5 rounded-xl"
                       style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Image size={14} style={{ color: SYSTEM_COLORS.textSoft, flexShrink: 0 }} />
                      <span className="text-sm font-medium truncate" style={{ color: NEUTRAL_COLORS.black }}>
                        {t.name || t.title || 'Untitled'}
                      </span>
                    </div>
                    <span className="text-xs flex-shrink-0 ml-3" style={{ color: SYSTEM_COLORS.textMuted }}>
                      {fmtDate(t.updatedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Requests */}
            <div className="rounded-xl border overflow-hidden"
                 style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              <div className="flex items-center justify-between px-5 py-3.5 border-b"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Design Requests</h2>
                <button
                  onClick={() => setActiveTab('requests')}
                  className="text-xs font-medium"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  View all →
                </button>
              </div>
              <div className="p-4 space-y-2">
                {overviewLoading ? (
                  [0,1,2].map(i => (
                    <div key={i} className="h-10 rounded-xl animate-pulse"
                         style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }} />
                  ))
                ) : recentRequests.length === 0 ? (
                  <p className="text-sm text-center py-6" style={{ color: SYSTEM_COLORS.textMuted }}>
                    No requests yet —{' '}
                    <button onClick={() => setActiveTab('requests')} style={{ color: BRAND_COLORS.primary }}>
                      file a brief
                    </button>
                  </p>
                ) : recentRequests.map((r) => (
                  <div key={r.id}
                       className="flex items-center justify-between px-3 py-2.5 rounded-xl"
                       style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                    <div className="min-w-0 mr-2">
                      <p className="text-sm font-medium truncate" style={{ color: NEUTRAL_COLORS.black }}>
                        {r.title}
                      </p>
                      {r.dueDate && (
                        <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                          Due {fmtDate(r.dueDate)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {r.priority && r.priority !== 'medium' && (
                        <span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize"
                              style={priorityStyle(r.priority)}>
                          {r.priority}
                        </span>
                      )}
                      <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize"
                            style={statusStyle(r.status)}>
                        {(r.status || '').replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance */}
            <div className="rounded-xl border overflow-hidden"
                 style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              <div className="px-5 py-3.5 border-b"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Performance</h2>
              </div>
              <div className="p-4 space-y-4">
                {overviewLoading ? (
                  [0,1,2].map(i => (
                    <div key={i} className="space-y-1.5">
                      <div className="h-3 w-32 rounded animate-pulse" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
                      <div className="h-1.5 rounded-full animate-pulse" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
                    </div>
                  ))
                ) : [
                  { label: 'Approval Rate',    value: `${approvalRate}%`,   pct: approvalRate   },
                  { label: 'Completion Rate',  value: `${completionRate}%`, pct: completionRate },
                  { label: 'Active Workload',  value: `${inProgressRate}%`, pct: inProgressRate },
                ].map(({ label, value, pct }) => (
                  <div key={label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</span>
                      <span className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{value}</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                      <div className="h-1.5 rounded-full transition-all"
                           style={{ width: `${pct}%`, backgroundColor: BRAND_COLORS.primary }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Brand quick view */}
            <div className="rounded-xl border overflow-hidden"
                 style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              <div className="flex items-center justify-between px-5 py-3.5 border-b"
                   style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Brand Quick View</h2>
                <button onClick={() => setActiveTab('brand')}
                  className="text-xs font-medium" style={{ color: BRAND_COLORS.primary }}>
                  See full brand →
                </button>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                {[
                  { icon: Droplets, label: 'Colour Palette', desc: 'Primary & secondary colours' },
                  { icon: FileText, label: 'Typography',     desc: 'Font styles and usage'       },
                  { icon: Image,    label: 'Logo Guidelines',desc: 'Clear space & colour rules'  },
                  { icon: Upload,   label: 'Voice & Tone',   desc: 'Writing style framework'     },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="p-3 rounded-xl cursor-pointer hover:shadow-sm transition-shadow"
                       style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
                       onClick={() => setActiveTab('brand')}>
                    <Icon size={16} className="mb-1.5" style={{ color: BRAND_COLORS.primary }} />
                    <p className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{label}</p>
                    <p className="text-xs mt-0.5 leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Requests ────────────────────────────────────────────────── */}
      {activeTab === 'requests' && <AssetRequests />}

      {/* ── Asset Library ────────────────────────────────────────────── */}
      {activeTab === 'library' && <DesignTemplateLibrary filterInspiration={false} />}

      {/* ── Inspiration ──────────────────────────────────────────────── */}
      {activeTab === 'inspiration' && <DesignTemplateLibrary filterInspiration={true} />}

      {/* ── Brand ───────────────────────────────────────────────────── */}
      {activeTab === 'brand' && <BrandStandards />}
    </div>
  );
};

export default GraphicsPage;
